/**
 * Evidence Form Component
 * 
 * Form for adding or editing evidence.
 * Part of Workstream 2: Evidence System
 */

import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEvidenceStore } from '../../../stores/prioritization/evidenceStore';
import type { 
  Evidence, 
  EvidenceType, 
  EvidenceReliability, 
  CreateEvidenceInput,
  AttributeEvidenceBinding,
} from '../../../types/prioritization/evidence';
import { getEvidenceTypeLabel, getReliabilityLabel } from '../../../types/prioritization/evidence';

const EVIDENCE_TYPES: EvidenceType[] = [
  'data',
  'research', 
  'expert_opinion',
  'customer_feedback',
  'market_signal',
  'competitor_intel',
  'internal_observation',
  'assumption',
];

const RELIABILITY_LEVELS: EvidenceReliability[] = [
  'verified',
  'corroborated',
  'single_source',
  'anecdotal',
  'speculative',
];

interface EvidenceFormProps {
  itemId: string;
  evidence?: Evidence;
  /** Value dimensions the evidence can be tied to (the item page's attributes). Omit to hide the choice. */
  attributeOptions?: { key: string; label: string }[];
  onSave?: (evidence: Evidence) => void;
  onCancel?: () => void;
}

export function EvidenceForm({
  itemId,
  evidence,
  attributeOptions,
  onSave,
  onCancel,
}: EvidenceFormProps) {
  const userId = useUserStore((s) => s.userId);
  // Which value dimensions this evidence bears on (owner, 2026-10-15): one or more.
  const [boundKeys, setBoundKeys] = useState<string[]>(() => [...new Set((evidence?.attributeBindings ?? []).map((b) => b.attributeKey))]);
  const toggleKey = (key: string) => setBoundKeys((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  /** Keeps an existing link as it was; adds a plain one for a newly chosen dimension. */
  const bindingsFor = (evidenceId: string): AttributeEvidenceBinding[] =>
    boundKeys.map(
      (key) =>
        (evidence?.attributeBindings ?? []).find((b) => b.attributeKey === key) ?? {
          id: uuidv4(),
          evidenceId,
          attributeKey: key,
          valueSupported: null,
          strength: 'moderate',
          createdAt: new Date(),
          createdBy: userId ?? 'unknown',
        },
    );
  const { addEvidence, updateEvidence } = useEvidenceStore();
  
  const [formData, setFormData] = useState<CreateEvidenceInput>({
    itemId,
    type: evidence?.type || 'data',
    reliability: evidence?.reliability || 'single_source',
    source: evidence?.source || '',
    sourceType: evidence?.sourceType || '',
    link: evidence?.link || '',
    supportsClaim: evidence?.supportsClaim || '',
    summary: evidence?.summary || '',
    rawContent: evidence?.rawContent || '',
    tags: evidence?.tags || [],
  });
  
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.source.trim()) {
      newErrors.source = 'Source is required';
    }
    if (!formData.summary.trim()) {
      newErrors.summary = 'Summary is required';
    }
    if (formData.summary.length < 10) {
      newErrors.summary = 'Summary must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    if (evidence) {
      updateEvidence(evidence.id, {
        type: formData.type,
        reliability: formData.reliability,
        source: formData.source,
        sourceType: formData.sourceType,
        link: formData.link,
        supportsClaim: formData.supportsClaim,
        summary: formData.summary,
        rawContent: formData.rawContent,
        tags: formData.tags,
        ...(attributeOptions ? { attributeBindings: bindingsFor(evidence.id) } : {}),
      });
      onSave?.(evidence);
    } else {
      // addEvidence assigns the id and re-points each binding's evidenceId at it.
      const newEvidence = addEvidence({ ...formData, ...(attributeOptions ? { attributeBindings: bindingsFor('') } : {}) });
      onSave?.(newEvidence);
    }
  };
  
  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput('');
    }
  };
  
  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag) || [],
    }));
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Evidence Type
        </label>
        <select
          value={formData.type}
          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as EvidenceType }))}
          className="bg-[var(--bg-raised)] text-[var(--text-pri)] mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
        >
          {EVIDENCE_TYPES.map(type => (
            <option key={type} value={type}>
              {getEvidenceTypeLabel(type)}
            </option>
          ))}
        </select>
        {formData.type === 'assumption' && (
          <p className="mt-1 text-xs text-[var(--mustard)]">
            Assumptions should be validated with actual evidence when possible.
          </p>
        )}
      </div>
      
      {/* Reliability */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Reliability
        </label>
        <select
          value={formData.reliability}
          onChange={(e) => setFormData(prev => ({ ...prev, reliability: e.target.value as EvidenceReliability }))}
          className="bg-[var(--bg-raised)] text-[var(--text-pri)] mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
        >
          {RELIABILITY_LEVELS.map(level => (
            <option key={level} value={level}>
              {getReliabilityLabel(level)}
            </option>
          ))}
        </select>
      </div>
      
      {/* Source */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Source *
        </label>
        <input
          type="text"
          value={formData.source}
          onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
          placeholder="e.g., Customer survey Q4 2024, Product analytics dashboard"
          className={`mt-1 block w-full  shadow-sm sm:text-sm ${
            errors.source ? 'border-[var(--coral)] focus:border-[var(--coral)] focus:ring-red-500' : 'border-[var(--rule)] focus:border-[var(--slate)] focus:ring-indigo-500'
          }`}
        />
        {errors.source && (
          <p className="mt-1 text-xs text-[var(--coral)]">{errors.source}</p>
        )}
      </div>
      
      {/* Summary */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Summary *
        </label>
        <textarea
          value={formData.summary}
          onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
          rows={2}
          placeholder="Brief description of what this evidence shows"
          className={`mt-1 block w-full  shadow-sm sm:text-sm ${
            errors.summary ? 'border-[var(--coral)] focus:border-[var(--coral)] focus:ring-red-500' : 'border-[var(--rule)] focus:border-[var(--slate)] focus:ring-indigo-500'
          }`}
        />
        {errors.summary && (
          <p className="mt-1 text-xs text-[var(--coral)]">{errors.summary}</p>
        )}
      </div>
      
      {/* Supports Claim */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Supports Claim
        </label>
        <input
          type="text"
          value={formData.supportsClaim}
          onChange={(e) => setFormData(prev => ({ ...prev, supportsClaim: e.target.value }))}
          placeholder="What claim or hypothesis does this evidence support?"
          className="mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
        />
      </div>
      
      {/* Value dimensions */}
      {attributeOptions && attributeOptions.length > 0 && (
        <fieldset>
          <legend className="block text-sm font-medium text-[var(--text-sec)]">Bears on which values</legend>
          <p className="mt-0.5 text-xs text-[var(--text-ter)]">Choose one or more. It then counts as evidence for each in Assessment.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {attributeOptions.map((o) => {
              const on = boundKeys.includes(o.key);
              return (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleKey(o.key)}
                  className={`border px-2.5 py-1 text-sm ${on ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'}`}
                >
                  {on ? '✓ ' : ''}
                  {o.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* Link */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Link (optional)
        </label>
        <input
          type="url"
          value={formData.link}
          onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
          placeholder="https://..."
          className="mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
        />
      </div>
      
      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-sec)]">
          Tags
        </label>
        <div className="mt-1 flex items-center space-x-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            placeholder="Add a tag"
            className="block flex-1 border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
          />
          <button
            type="button"
            onClick={addTag}
            className="bg-[var(--hover-tint)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
          >
            Add
          </button>
        </div>
        {formData.tags && formData.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {formData.tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs text-[var(--text-sec)]"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-1 text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)]"
        >
          {evidence ? 'Save Changes' : 'Add Evidence'}
        </button>
      </div>
    </form>
  );
}
